/**
 * Token storage.
 *
 * Split on purpose, and the split is load-bearing across both platforms: the
 * access token lives in a module variable and dies with the process, while the
 * refresh token persists through the platform's `SecureStore`. The refresh
 * window is 30 days (spec §5) because Sarah needs to work a week offline and
 * still drain when she reconnects — so persisting *something* is not optional.
 * Persisting the access token too would buy nothing and widen the blast radius
 * of an XSS from "one hour" to "30 days".
 *
 * `getAccessToken` stays **synchronous**. It is read on every request, and
 * every native secure store is async — making it async would push an await
 * into the shared transport's hot path and force a rewrite of the client all
 * four apps use. Keeping the access token in memory is what lets it stay sync
 * on a phone as well as in a tab.
 */
import { platform } from '@kapture/platform';

const REFRESH_KEY = 'refresh_token';
const PROFILE_KEY = 'profile';

export interface TokenPair {
  accessToken: string;
  refreshToken: string;
  /** Seconds until the access token expires, straight from the server. */
  accessExpiresIn: number;
}

let accessToken: string | null = null;
let accessExpiresAt: number | null = null;

export function getAccessToken(): string | null {
  return accessToken;
}

/**
 * True when there is an access token that has not expired. A 60s skew keeps us
 * from sending a token that dies in flight.
 */
export function hasFreshAccessToken(now: number = Date.now()): boolean {
  if (!accessToken || accessExpiresAt == null) return false;
  return accessExpiresAt - 60_000 > now;
}

export function setAccessToken(token: string | null, expiresInSeconds?: number): void {
  accessToken = token;
  accessExpiresAt =
    token && expiresInSeconds != null ? Date.now() + expiresInSeconds * 1000 : null;
}

export async function setTokens(pair: TokenPair): Promise<void> {
  setAccessToken(pair.accessToken, pair.accessExpiresIn);
  await platform().secureStore.set(REFRESH_KEY, pair.refreshToken);
}

export async function loadRefreshToken(): Promise<string | null> {
  return platform().secureStore.get(REFRESH_KEY);
}

/**
 * Cached user profile.
 *
 * Booting offline means `/auth/refresh` cannot be reached, and treating that
 * as "signed out" would lock Sarah out of an offline-first app while her queue
 * still holds unsent work. The cached profile lets the app come up in a signed-
 * in state; requests still 401 until a refresh succeeds, and the queue treats
 * that as retryable.
 */
export async function saveProfile(profile: unknown): Promise<void> {
  await platform().secureStore.set(PROFILE_KEY, JSON.stringify(profile));
}

export async function loadProfile<T>(): Promise<T | null> {
  const raw = await platform().secureStore.get(PROFILE_KEY);
  if (raw == null) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    // A profile we cannot parse is a profile we do not have. Better to fall
    // back to "sign in again" than to crash the boot path on stored garbage.
    return null;
  }
}

export async function clearTokens(): Promise<void> {
  setAccessToken(null);
  await platform().secureStore.delete(REFRESH_KEY);
  await platform().secureStore.delete(PROFILE_KEY);
}
